import { format as d3Format } from 'd3';

export const isNullOrUndefined = (value: unknown) => value === null || value === undefined;

export const iconSize = (size = 20) => ({ width: size, height: size });

export const getCleanNumberInputValue = (value: any) =>
  (!value.length && typeof value !== 'number') || Number.isNaN(+value) ? null : parseFloat(value);

export const getMitreLink = (techniqueId: string) => `https://attack.mitre.org/techniques/${techniqueId}`;

export const generateOption = (value: any) => ({ title: value, value });

export const abbreviateNumber = (val: any) => d3Format(val < 1 && val > 0 ? '.2f' : '.3~s')(val).replace('G', 'B');

export const uniqBy = (arr, predicate, shouldFilter: boolean = true) => {
  if (!Array.isArray(arr)) {
    return [];
  }

  const cb = typeof predicate === 'function' ? predicate : o => o[predicate];

  const pickedObjects = arr
    .filter(item => (shouldFilter ? item : true))
    .reduce((map, item) => {
      const key = cb(item);

      if (key === undefined) {
        return map;
      }

      return map.has(key) ? map : map.set(key, item);
    }, new Map())
    .values();

  return [...pickedObjects];
};

export const arrayEquals = (a, b, compareItems = (itemA, itemB) => itemA === itemB) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => compareItems(val, b[index]));

const isObjectOrArray = object => object != null && typeof object === 'object';

export const isObject = object => object !== null && typeof object === 'object' && !Array.isArray(object);

export const isDeepEqual = (object1 = {}, object2 = {}, ignoreFields: string[] = []) => {
  const objKeys1 = Object.keys(object1).filter(key => !ignoreFields.includes(key));
  const objKeys2 = Object.keys(object2).filter(key => !ignoreFields.includes(key));

  if (objKeys1.length !== objKeys2.length) {
    return false;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const key of objKeys1) {
    const value1 = object1[key];
    const value2 = object2[key];

    const isObjects = isObjectOrArray(value1) && isObjectOrArray(value2);

    if ((isObjects && !isDeepEqual(value1, value2, ignoreFields)) || (!isObjects && value1 !== value2)) {
      return false;
    }
  }
  return true;
};

const getFilteredList = (list, item, filterKey) =>
  list.filter(v => (filterKey === undefined ? v !== item : v[filterKey] !== item[filterKey]));

export const rearrange = (list, item, newIndex, filterKey, hasMoved = false, isTableV2 = false) => {
  const filteredList = getFilteredList(list, item, filterKey);
  const newItem =
    filterKey === undefined
      ? item
      : isTableV2
        ? { ...item, meta: { ...item?.meta, hidden: hasMoved ? !item.meta?.hidden : item.meta?.hidden } }
        : { ...item, hidden: hasMoved ? !item.hidden : item.hidden };

  if (newIndex === list.length - (hasMoved ? 0 : 1)) {
    return [...filteredList, newItem];
  }
  return (hasMoved ? list : filteredList).reduce(
    (arr, el, index) =>
      [...arr, index === newIndex && newItem, (filterKey === undefined ? el !== item : el[filterKey] !== item[filterKey]) && el].filter(
        d => d
      ),
    []
  );
};

export const isEquals = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return arrayEquals(a, b);
  }
  if (typeof a === 'object' && typeof b === 'object') {
    return isDeepEqual(a, b);
  }
  return a === b;
};

/* eslint-disable no-bitwise */
export function stringToColor(string, colors) {
  // eslint-disable-next-line fabric-ui/no-let
  let hash = 0;
  // eslint-disable-next-line fabric-ui/no-let
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(hash);
  // eslint-disable-next-line fabric-ui/no-let
  let color = '#';
  // choose random color
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  // choose a color from the list
  const colorIndex = hash % colors.length;
  return colors[colorIndex] || color;
}
/* eslint-enable no-bitwise */

export const getIfValidStringifiedObject = (str: string, includeArrays = false) => {
  try {
    const obj = JSON.parse(decodeURIComponent(str));
    if (includeArrays ? typeof obj !== 'object' : !isObject(obj)) {
      return false;
    }
    return obj;
  } catch {
    return false;
  }
};

export const flattenObject = ({ obj, prefix = '', res = {}, shouldFlattenArray = true }) =>
  Object.entries(obj).reduce((r, [key, val]) => {
    const k = prefix ? (Array.isArray(obj) ? `${prefix}[${key}]` : `${prefix}.${key}`) : key;
    if (val && typeof val === 'object' && (shouldFlattenArray || !Array.isArray(val))) {
      const isEmptyArray = Array.isArray(val) && !val.length;
      if (isEmptyArray) {
        res[k] = JSON.stringify(val);
      } else {
        flattenObject({ obj: val, prefix: `${k}`, res: r, shouldFlattenArray });
      }
    } else {
      res[k] = val;
    }
    return r;
  }, res);

export const renameProp = (oldProp, newProp, { [oldProp]: old, ...others }) =>
  structuredClone({
    [newProp]: old,
    ...others,
  });

export const capitalizeSentence = sentence => sentence.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
